import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api/client.js';

/**
 * DNS auto preview (2026-10-23) — what the panel would write to a site's DNS,
 * against the live zone. Shared by Create site, Static site add and both
 * deletes ("also remove its DNS records"). Nothing is written from here: the
 * page sends the preview's fingerprint with its own confirm.
 */

const ACTION_CLS = {
  ADD: 'bg-s-blue text-[#2E5C8A]',
  EDIT: 'bg-warn-bg text-[#8B5206]',
  KEEP: 'bg-s-3 text-t2',
  CONFLICT: 'bg-err-bg text-err',
  REMOVE: 'bg-err-bg text-err',
};

const DNS_AT = { spaceship: 'Spaceship', cloudflare: 'Cloudflare', other: 'another DNS host', unknown: 'an unknown DNS host' };

/** POST/GET the preview for `domain` once the inputs settle. `request` null = nothing to fetch. */
export function useDnsPreview(request) {
  const [state, setState] = useState({ loading: false, preview: null, error: null });
  const key = request ? JSON.stringify(request) : null;
  useEffect(() => {
    if (!key) { setState({ loading: false, preview: null, error: null }); return undefined; }
    const req = JSON.parse(key);
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    const t = setTimeout(() => {
      api(req.path, req.body ? { method: 'POST', body: req.body } : {})
        .then((preview) => { if (live) setState({ loading: false, preview, error: null }); })
        .catch((err) => { if (live) setState({ loading: false, preview: null, error: err.body?.detail ?? err.message }); });
    }, req.delay ?? 500);
    return () => { live = false; clearTimeout(t); };
  }, [key]);
  return state;
}

export function DnsPreview({ loading, preview, error, title = 'DNS' }) {
  if (loading && !preview) {
    return (
      <div className="flex items-center gap-2 text-xs text-t3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the domain's DNS…
      </div>
    );
  }
  if (error) return <p className="text-xs text-err">Could not read the DNS: {error}</p>;
  if (!preview) return null;
  const p = preview;
  return (
    <div className="space-y-2">
      <div className="text-xs text-t2">
        <span className="font-semibold text-t1">{title}</span>
        {p.zone && <> · zone <span className="font-mono">{p.zone}</span></>}
        {p.dns_at && <> · DNS at {DNS_AT[p.dns_at] ?? p.dns_at}</>}
        {p.account && <> · account “{p.account.label}”</>}
        {p.ip && <> · IP <span className="font-mono">{p.ip}</span>{p.ip_source ? ` (${p.ip_source})` : ''}</>}
        {loading && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}
      </div>
      {p.detail && (
        <p className={`text-xs ${p.state === 'ok' ? 'text-t3' : 'text-[#8B5206]'}`}>{p.detail}</p>
      )}
      {p.rows?.length > 0 && <DnsRows rows={p.rows} />}
    </div>
  );
}

export function DnsRows({ rows }) {
  return (
    <div className="overflow-x-auto rounded-md border border-bd">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.action}-${r.type}-${r.name}-${r.current?.join()}`} className="border-b border-bd last:border-0 align-top">
              <td className="px-2 py-1.5 w-20">
                <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${ACTION_CLS[r.action] ?? 'bg-s-3 text-t2'}`}>{r.action}</span>
              </td>
              <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.type} {r.fqdn ?? r.name}</td>
              <td className="px-2 py-1.5 font-mono break-all">
                {r.action === 'EDIT' && <>{r.current.join(', ')} → </>}
                {r.action === 'ADD' && r.proposed}
                {r.action === 'EDIT' && r.proposed}
                {['KEEP', 'CONFLICT', 'REMOVE'].includes(r.action) && r.current.join(', ')}
                {r.detail && <span className="block font-sans text-t3">{r.detail}</span>}
                {r.warnings?.map((w) => (
                  <span key={w.text} className={`block font-sans ${w.level === 'danger' ? 'text-err font-medium' : 'text-[#8B5206]'}`}>{w.text}</span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One line for a job's / an apply's DNS outcome. */
export function DnsOutcome({ dns }) {
  if (!dns) return null;
  if (dns.ok) {
    const n = dns.removed?.length ?? 0;
    const a = dns.added?.length ?? 0;
    return (
      <p className="text-xs text-ok">
        DNS: {a ? `${a} record(s) written` : ''}{a && n ? ', ' : ''}{n ? `${n} record(s) removed` : ''}{!a && !n ? 'nothing to change' : ''} — read back and confirmed.
      </p>
    );
  }
  const mism = dns.mismatches?.length ? ` (${dns.mismatches.join('; ')})` : '';
  return (
    <p className="text-xs text-err">
      DNS not done: {dns.detail ?? dns.error?.detail ?? dns.error?.code ?? dns.status ?? 'not confirmed'}{mism}
    </p>
  );
}
